import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'
import runtimeConfig from '../config/runtime.config.js'
import { websiteConfig, appConfigData } from '../../app.config.js'

export const useConfigStore = defineStore('config', () => {
  // websiteConfig/appConfigData datang dari app.config.js->getClientConfig(),
  // yang baca langsung dari import.meta.env di browser. Vite HANYA
  // menerapkan static replacement (define: __WEBSITE_TITLE__ dkk) saat build
  // - selama `vite dev` define itu no-op di sisi client, jadi __WEBSITE_TITLE__
  // dkk SELALU 'undefined' pas dev meski .env sudah diisi. import.meta.env
  // sebaliknya beneran jalan di dev maupun build (dibuktikan apiConfig.baseUrl
  // yang sudah lama dipakai di seluruh app lewat jalur yang sama).
  const viteConfig = { website: websiteConfig, app: appConfigData }

  // Default configuration
  const defaultConfig = {
    website: { ...websiteConfig },
    app: { ...appConfigData },
  }

  // Reactive configuration state
  const config = reactive({ ...defaultConfig })
  const isLoading = ref(false)
  const error = ref(null)

  // Load configuration from multiple sources
  async function loadConfig() {
    isLoading.value = true
    error.value = null
    
    try {
      // Get runtime configuration (includes localStorage)
      const runtimeConfigData = runtimeConfig.getAll()
      
      // Merge configurations: default -> vite -> runtime
      const mergedConfig = {
        ...defaultConfig,
        ...viteConfig,
        ...runtimeConfigData,
        website: {
          ...defaultConfig.website,
          ...viteConfig.website,
          ...runtimeConfigData.website,
          colors: {
            ...defaultConfig.website.colors,
            ...viteConfig.website?.colors,
            ...runtimeConfigData.website?.colors
          }
        },
        app: {
          ...defaultConfig.app,
          ...viteConfig.app,
          ...runtimeConfigData.app
        }
      }
      
      // Update reactive config
      Object.assign(config, mergedConfig)
      
      // Apply CSS custom properties for colors
      applyColors()
      
    } catch (err) {
      error.value = err.message
      console.warn('Failed to load configuration, using defaults:', err)
    } finally {
      isLoading.value = false
    }
  }

  // Apply colors to CSS custom properties
  function applyColors() {
    const root = document.documentElement
    root.style.setProperty('--primary-color', config.website.colors.primary)
    root.style.setProperty('--secondary-color', config.website.colors.secondary)
    root.style.setProperty('--accent-color', config.website.colors.accent)
    root.style.setProperty('--active-color', config.website.colors.accent)
  }

  // Update configuration
  function updateConfig(newConfig) {
    // Update runtime configuration
    runtimeConfig.update(newConfig)
    
    // Update reactive config
    Object.assign(config, {
      ...config,
      ...newConfig,
      website: {
        ...config.website,
        ...newConfig.website,
        colors: {
          ...config.website.colors,
          ...newConfig.website?.colors
        }
      },
      app: {
        ...config.app,
        ...newConfig.app
      }
    })
    
    // Apply colors immediately
    applyColors()
    
    // Update page meta
    runtimeConfig.updatePageMeta()
  }

  // Reset to default configuration
  function resetConfig() {
    // Reset runtime configuration
    runtimeConfig.reset()
    
    // Reset reactive config
    Object.assign(config, defaultConfig)
    
    // Apply colors immediately
    applyColors()
    
    // Update page meta
    runtimeConfig.updatePageMeta()
  }

  // Getters
  const websiteTitle = () => config.website.title
  const websiteSubtitle = () => config.website.subtitle
  const websiteIcon = () => config.website.icon
  const websiteLogo = () => config.website.logo
  const primaryColor = () => config.website.colors.primary
  const secondaryColor = () => config.website.colors.secondary
  const accentColor = () => config.website.colors.accent

  return {
    // State
    config,
    isLoading,
    error,
    
    // Actions
    loadConfig,
    updateConfig,
    resetConfig,
    applyColors,
    
    // Getters
    websiteTitle,
    websiteSubtitle,
    websiteIcon,
    websiteLogo,
    primaryColor,
    secondaryColor,
    accentColor
  }
})
